/*
 * Presentación del dashboard de Marcaciones (respuesta de GET /attendance/dashboard/, etapa 3): formato
 * de los KPI y de su comparación con el periodo anterior, datos listos para las gráficas y el resumen
 * ejecutivo para gerencia. Funciones puras (dashboard.test.js); no dependen de la librería de gráficas.
 *
 * Contrato que se consume (datos): periodo, periodo_anterior, parametros {hora_oficial, tolerancia_min,
 * almuerzo_max_min, minimo_jornadas_ranking}, kpis [{clave, titulo, unidad ('%' | 'hora' | 'h' | 'min' |
 * 'jornadas'), mejor_si, valor, anterior, diferencia, variacion_pct, tendencia ('mejora' | 'empeora' |
 * 'igual' | null), serie [{fecha, valor}]}], rankings {mas_puntuales, mas_atrasos, llegan_primero},
 * distribucion_llegada [{tramo, etiqueta, cantidad, porcentaje}], mapa_calor {bloques, series [{dia, datos}]},
 * tendencia_semanal, por_tienda, por_zona, horas_por_persona, alertas {total, por_tipo, detalle}.
 * La unidad 'hora' viaja en minutos desde la medianoche (484 = 08:04) y su diferencia también en minutos.
 * anterior, diferencia, variacion_pct y tendencia pueden ser null (periodo anterior sin datos): se muestra
 * 'Sin comparación', nunca 0. Los valores del servidor se muestran tal cual (sin recalcular).
 */
import {durationLabel, formatNumber, minutesToClock} from './rows';
import {addDays, shortDayLabel} from './period';

const TONES = {mejora: 'up', empeora: 'down', igual: 'neutral'};
// Texto de la tendencia además del color (WCAG 1.4.1): lector de pantalla y title.
const TREND_TEXT = {mejora: 'mejora', empeora: 'empeora', igual: 'sin cambio'};

export const BAND_COLORS = {
    antes_0730: '#2fa06d',
    '0730_0800': '#2b8f9e',
    '0800_0810': '#5a8dee',
    despues_0810: '#e0705a',
};

// Tonos con contraste no textual >= 3:1 sobre blanco. El de 'sin entradas' es a propósito claro (es ausencia, no dato):
// su significado va en la leyenda con texto.
const HEAT_COLORS = ['#5f8ce6', '#4b7bd8', '#3768c8', '#1f4596'];
const HEAT_EMPTY = '#e6ebf3';

const hasValue = (value) => value !== null && value !== undefined;

/** Escapa & < > " ' : ApexCharts arma sus tooltips con innerHTML y los nombres vienen del API (texto libre). */
export function escapeHtml(text) {
    return String(text === null || text === undefined ? '' : text).replace(/[&<>"']/g, (char) => (
        {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[char]));
}

/** Valor de un KPI con su unidad: '75,0 %', '08:04', '7,0 h', '58 min', '3'. */
export function formatKpiValue(unit, value) {
    if (!hasValue(value)) return '—';
    if (unit === '%') return `${formatNumber(value, 1)} %`;
    if (unit === 'hora') return minutesToClock(value);
    if (unit === 'h') return `${formatNumber(value, 1)} h`;
    if (unit === 'min') return `${Math.round(value)} min`;
    return String(value);
}

function signed(text, value) {
    if (value > 0) return `+${text}`;
    if (value < 0) return `−${text}`;
    return text;
}

function formatDifference(unit, difference) {
    const amount = Math.abs(difference);
    if (unit === '%') return signed(`${formatNumber(amount, 1)} pts`, difference);
    if (unit === 'h') return signed(`${formatNumber(amount, 1)} h`, difference);
    if (unit === 'min') return signed(`${Math.round(amount)} min`, difference);
    return signed(String(Math.round(amount)), difference);
}

/**
 * Comparación de un KPI para la tarjeta: tone ('up' mejora, 'down' empeora, 'neutral' igual, 'muted' sin
 * dato), direction ('sube' | 'baja' | 'igual' | null), trend ('mejora' | 'empeora' | 'sin cambio' | null), text corto y label con el valor anterior.
 * Porcentaje de variación cuando se puede; la hora de llegada se expresa en minutos más temprano/tarde.
 */
export function kpiDelta(card) {
    if (!card.tendencia || !hasValue(card.diferencia)) {
        return {tone: 'muted', direction: null, trend: null, text: 'Sin comparación', label: 'No hay datos del periodo anterior para comparar'};
    }
    const direction = card.diferencia > 0 ? 'sube' : (card.diferencia < 0 ? 'baja' : 'igual');
    let text;
    if (direction === 'igual') {
        text = 'Igual';
    } else if (card.unidad === 'hora') {
        text = `${Math.round(Math.abs(card.diferencia))} min más ${direction === 'sube' ? 'tarde' : 'temprano'}`;
    } else if (hasValue(card.variacion_pct)) {
        text = signed(`${formatNumber(Math.abs(card.variacion_pct), 1)} %`, card.variacion_pct);
    } else {
        text = formatDifference(card.unidad, card.diferencia);
    }
    return {
        tone: TONES[card.tendencia],
        direction,
        trend: TREND_TEXT[card.tendencia],
        text,
        label: `${text} frente al periodo anterior (${formatKpiValue(card.unidad, card.anterior)})`,
    };
}

/** Frase de con qué periodo se compara (SummaryPanel). Sin jornadas en el periodo anterior no hay comparación. */
export function comparisonNote(previous, inProgress) {
    if (!previous) return '';
    const tail = inProgress ? ' El periodo actual aún no termina: sus datos llegan hasta hoy.' : '';
    if (previous.jornadas === 0) return `Sin periodo anterior con datos para comparar.${tail}`;
    const jornadas = typeof previous.jornadas === 'number' ? `, ${previous.jornadas} jornadas` : '';
    return `Se compara con ${shortDayLabel(previous.fecha_desde)} – ${shortDayLabel(previous.fecha_hasta)} (mismo número de días${jornadas}).${tail}`;
}

/** Valores de la minigráfica: solo los días con dato (fines de semana y días sin marcas se saltan). */
export function sparklineValues(serie) {
    return serie.filter((point) => hasValue(point.valor)).map((point) => point.valor);
}

/** Datos de la gráfica de tramos de llegada. */
export function distributionChart(distribucion) {
    return {
        categories: distribucion.map((row) => row.etiqueta),
        values: distribucion.map((row) => row.cantidad),
        colors: distribucion.map((row) => BAND_COLORS[row.tramo]),
        label: distribucion.map((row) => (hasValue(row.porcentaje)
            ? `${row.etiqueta}: ${row.cantidad} (${formatKpiValue('%', row.porcentaje)})`
            : `${row.etiqueta}: ${row.cantidad}`)).join('. '),
    };
}

/**
 * Series del mapa de calor para ApexCharts: una fila por día con {x: bloque, y: entradas}. ApexCharts
 * dibuja la primera serie abajo, por eso se invierte (Lunes queda arriba).
 */
export function heatmapSeries(mapa) {
    return [...mapa.series].reverse().map((row) => ({
        name: row.dia,
        data: row.datos.map((value, index) => ({x: mapa.bloques[index], y: value})),
    }));
}

/** Máximo de entradas en un bloque (para la escala de color). */
export function heatmapMax(mapa) {
    return mapa.series.reduce((max, row) => Math.max(max, ...row.datos), 0);
}

/** Escala de color del mapa de calor: 0 en gris claro y 4 tonos de azul hasta el máximo. */
export function heatmapRanges(max) {
    const ranges = [{from: 0, to: 0, color: HEAT_EMPTY, name: 'Sin entradas'}];
    const step = Math.max(1, Math.ceil(max / HEAT_COLORS.length));
    HEAT_COLORS.forEach((color, index) => {
        const from = index * step + 1;
        const top = Math.max(max, from);
        const to = index === HEAT_COLORS.length - 1 ? top : Math.min((index + 1) * step, top);
        if (from <= Math.max(max, 1)) ranges.push({from, to, color, name: from === to ? `${from}` : `${from} a ${to}`});
    });
    return ranges;
}

/** Texto del mapa de calor para lectores de pantalla: el bloque con más entradas de cada día. */
export function heatmapLabel(mapa) {
    if (!mapa.series.length) return 'Sin entradas en el periodo';
    return mapa.series.map((row) => {
        const peak = row.datos.indexOf(Math.max(...row.datos));
        return `${row.dia}: más entradas a las ${mapa.bloques[peak]}`;
    }).join('. ');
}

/**
 * Recorta el mapa de calor a las horas con entradas: una entrada de madrugada (00:05) no debe estirar el eje
 * con horas vacías. Se conservan las horas completas con entradas y los huecos de hasta 2 horas entre ellas
 * (escala continua); un hueco de 3 horas o más se omite y se informa en `omitidas` [{desde, hasta, horas}]
 * (desde/hasta 'HH:00'; el eje queda no lineal y la pantalla lo avisa). Sin entradas devuelve el mapa igual.
 */
export function trimHeatmap(mapa) {
    const hourOf = (block) => Number(String(block).slice(0, 2));
    const pad = (hour) => `${String(hour).padStart(2, '0')}:00`;
    const withData = new Set();
    mapa.bloques.forEach((block, index) => {
        if (mapa.series.some((row) => row.datos[index] > 0)) withData.add(hourOf(block));
    });
    if (!withData.size) return {...mapa, omitidas: []};
    const sorted = [...withData].sort((x, y) => x - y);
    const keep = new Set(sorted);
    const omitidas = [];
    sorted.forEach((hour, index) => {
        const next = sorted[index + 1];
        if (next === undefined) return;
        if (next - hour <= 3) {
            for (let h = hour + 1; h < next; h += 1) keep.add(h);
        } else {
            omitidas.push({desde: pad(hour + 1), hasta: pad(next), horas: next - hour - 1});
        }
    });
    const indexes = mapa.bloques.map((block, index) => (keep.has(hourOf(block)) ? index : -1)).filter((index) => index >= 0);
    return {
        bloques: indexes.map((index) => mapa.bloques[index]),
        series: mapa.series.map((row) => ({...row, datos: indexes.map((index) => row.datos[index])})),
        omitidas,
    };
}

/** Aviso de horas omitidas del mapa recortado ('' si no se omitió nada). */
export function omittedHoursNotice(omitidas) {
    if (!omitidas || !omitidas.length) return '';
    const total = omitidas.reduce((sum, gap) => sum + gap.horas, 0);
    return `Se omiten ${total} ${total === 1 ? 'hora' : 'horas'} sin entradas (${omitidas.map((gap) => `${gap.desde}–${gap.hasta}`).join(', ')}).`;
}

/**
 * Una semana es parcial si empieza antes del periodo, termina después del ÚLTIMO DÍA TRANSCURRIDO o el
 * servidor no trae alguno de sus valores (null). Con periodo en curso `fecha_hasta` es el fin pedido (p. ej.
 * fin de mes) y solo `dias_transcurridos` marca el corte: último día = fecha_desde + dias_transcurridos - 1.
 */
function isPartialWeek(row, periodo) {
    if (row.puntualidad === null || row.asistencia === null || row.llegada_temprana === null) return true;
    if (!periodo) return false;
    const weekEnd = row.semana_fin || addDays(row.semana_inicio, 6);
    const lastDay = periodo.fecha_desde && hasValue(periodo.dias_transcurridos)
        ? addDays(periodo.fecha_desde, periodo.dias_transcurridos - 1) : periodo.fecha_hasta;
    return Boolean((periodo.fecha_desde && row.semana_inicio < periodo.fecha_desde) || (lastDay && weekEnd > lastDay));
}

/** Tendencia semanal: categorías (semana, con "parcial" cuando aplica) y tres series en % (null = hueco). */
export function weeklyTrendChart(tendencia, periodo) {
    return {
        categories: tendencia.map((row) => `${shortDayLabel(row.semana_inicio)}${isPartialWeek(row, periodo) ? ' (parcial)' : ''}`),
        series: [
            {name: 'Puntualidad', data: tendencia.map((row) => row.puntualidad)},
            {name: 'Asistencia', data: tendencia.map((row) => row.asistencia)},
            {name: 'Antes de las 08:00', data: tendencia.map((row) => row.llegada_temprana)},
        ],
    };
}

/** Texto de la tendencia para lectores de pantalla: una frase por semana, "sin dato" donde no hay valor. */
export function weeklyTrendLabel(chart) {
    return chart.categories.map((week, index) => `${week}: ${chart.series.map((serie) => `${serie.name} ${hasValue(serie.data[index])
        ? formatKpiValue('%', serie.data[index]) : 'sin dato'}`).join(', ')}`).join('. ');
}

/** Las `limit` personas con más horas (gráfica de barras horizontal). */
export function topHours(horas, limit = 15) {
    const top = horas.slice(0, limit);
    return {
        categories: top.map((row) => row.empleado),
        values: top.map((row) => row.horas),
        label: top.map((row) => `${row.empleado}: ${formatKpiValue('h', row.horas)}`).join('. '),
    };
}

/** Día de las barras de jornada del dashboard: el último día del rango que ya pasó (o null si es futuro). */
export function dayForBars(range, today) {
    if (range.desde > today) return null;
    return range.hasta < today ? range.hasta : today;
}

/** Resumen ejecutivo para gerencia: frases cortas con lo más importante del periodo. */
export function executiveSummary(datos) {
    const kpis = {};
    datos.kpis.forEach((card) => {
        kpis[card.clave] = card;
    });
    const official = datos.parametros.hora_oficial;
    const lines = [];
    const punctuality = kpis.puntualidad;
    if (punctuality && hasValue(punctuality.valor)) {
        const value = formatKpiValue('%', punctuality.valor);
        const before = formatKpiValue('%', punctuality.anterior);
        if (punctuality.tendencia === 'mejora') lines.push(`Puntualidad ${value}, mejor que el periodo anterior (${before}).`);
        else if (punctuality.tendencia === 'empeora') lines.push(`Puntualidad ${value}, peor que el periodo anterior (${before}).`);
        else if (punctuality.tendencia === 'igual') lines.push(`Puntualidad ${value}, igual que el periodo anterior.`);
        else lines.push(`Puntualidad ${value}.`);
    }
    const attendance = kpis.asistencia;
    if (attendance && hasValue(attendance.valor)) {
        lines.push(`Asistencia ${formatKpiValue('%', attendance.valor)} de las jornadas esperadas de lunes a viernes.`);
    }
    const first = datos.rankings.llegan_primero[0];
    if (first) {
        lines.push(`Llega primero: ${first.empleado}, ${formatNumber(first.adelanto_promedio_min, 1)} min antes de las ${official} en promedio.`);
    }
    const late = datos.rankings.mas_atrasos[0];
    if (late) lines.push(`Más atrasos: ${late.empleado}, ${durationLabel(late.atraso_total_min)} acumulados.`);
    if (datos.alertas.total) {
        lines.push(`${datos.alertas.total} ${datos.alertas.total === 1 ? 'jornada' : 'jornadas'} con marcas incompletas por revisar.`);
    }
    return lines;
}

/** Ancho (0-100) de la barra de puntualidad de un grupo: null -> 0 (la barra se oculta), fuera de rango se recorta. */
export function barWidth(pct) {
    if (!hasValue(pct)) return 0;
    return Math.max(0, Math.min(100, pct));
}

/**
 * Textos de una fila de tienda/zona (por_tienda / por_zona): personas y jornadas con singular/plural, y
 * puntualidad, asistencia y horas por persona formateadas ('—' cuando el servidor manda null, nunca 0).
 * `incompletas` es '' cuando no hay (la insignia solo aparece con > 0).
 */
export function groupLine(group) {
    const people = `${group.empleados} ${group.empleados === 1 ? 'persona' : 'personas'}`;
    const workdays = `${group.jornadas} ${group.jornadas === 1 ? 'jornada' : 'jornadas'}`;
    return {
        meta: `${people} · ${workdays}`,
        puntualidad: formatKpiValue('%', group.puntualidad),
        asistencia: formatKpiValue('%', group.asistencia),
        horas: formatKpiValue('h', group.horas_por_persona),
        incompletas: group.incompletas > 0 ? `${group.incompletas} ${group.incompletas === 1 ? 'incompleta' : 'incompletas'}` : '',
    };
}

/**
 * Rótulo de las alertas: 'Mostrando N de total jornadas con marcas incompletas'. `total` es el conteo real del
 * servidor y `detailLength` las filas de detalle que trajo (máx. 50): si es menor que el total se avisa del recorte.
 */
export function alertsCountText(total, shown, detailLength = shown) {
    const base = `Mostrando ${shown} de ${total} ${total === 1 ? 'jornada' : 'jornadas'} con marcas incompletas`;
    return detailLength < total
        ? `${base} (el detalle trae solo las ${detailLength} más recientes: acota el rango o los filtros).`
        : `${base}.`;
}

/** Fecha corta 'lun 21 sep'; con año ('lun 21 sep 2025') si el rango cruza años o la fecha es de otro año que el fin del rango. */
export function alertDayLabel(iso, range) {
    const year = iso.slice(0, 4);
    const withYear = range && (range.desde.slice(0, 4) !== range.hasta.slice(0, 4) || year !== range.hasta.slice(0, 4));
    return withYear ? `${shortDayLabel(iso)} ${year}` : shortDayLabel(iso);
}
